<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { ChevronLeftIcon, ChevronRightIcon, TrashIcon } from '@heroicons/vue/20/solid'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
import BigNumber from 'bignumber.js'
import { useCartStore } from '~/store/CartStore'
import { useMainStore } from '~/store/MainStore'
import type { BpCartItem, BpCartItemModifier } from '~/definitions/AllDefinitions'
import { cartItemSubTotal, getCartExtras, getCartOptions } from '~/classes/CartUtils'

interface CartItemInterface {
  item: BpCartItem
  displayDescription?: boolean
  disabled?: boolean
}
const props = withDefaults(defineProps<CartItemInterface>(), {
  displayDescription: false,
  disabled: false,
})
const emit = defineEmits(['remove-cart-item', 'update-quantity', 'updating-quantity'])
const { t } = useI18n()
const cartStore = useCartStore()

const { currencyCode } = storeToRefs(useMainStore())
const cartItem = ref({} as BpCartItem)
const extras = ref([] as BpCartItemModifier[])
const loading = ref(false)
const options = ref([] as BpCartItemModifier[])
const subTotal = ref(0)

const altText = computed(() => {
  return props.item.image && props.item.image.altText
    ? props.item.image.altText
    : props.item.name
})
const thumbnail = computed(() => {
  if (!(props.item && props.item.image && props.item.image.original)) {
    return 'default-product-thumbnail.svg'
  }
  const defImg = props.item.image.original
  const img = props.item.image
  return img.sizes
    ? img.sizes.s64
      ? img.sizes.s64
      : img.sizes.s60
        ? img.sizes.s60
        : img.sizes.s40
          ? img.sizes.s40
          : img.sizes.s32
            ? img.sizes.s32
            : img.sizes.s16
              ? img.sizes.s16
              : defImg
    : defImg
})
function setItem(item?: any) {
  setOption(item)
  setExtras(item)
  setSubTotal(item)
}
async function handleUpdateQuantity() {
  loading.value = true
  emit('updating-quantity', true)
  const res = await cartStore.updateCartItem({ quantity: cartItem.value.quantity }, props.item.uuid)
  if (res.success && res.data) {
    emit('update-quantity', cloneDeep(props.item), res.data.quantity)
  } else {
    // TODO error handling
    console.debug('error updating quantity', res.error)
  }
  loading.value = false
  emit('updating-quantity', false)
}
function setExtras(item?: BpCartItem | null) {
  if (item) {
    extras.value = getCartExtras(item)
  } else {
    extras.value.length = 0
  }
}
function setOption(item?: BpCartItem | null) {
  options.value = []
  if (item) {
    options.value = getCartOptions(item)
  }
}
function setSubTotal(item?: BpCartItem | null) {
  if (item) {
    subTotal.value = cartItemSubTotal(item)
  } else {
    subTotal.value = 0
  }
}
async function removeItem() {
  if (props.item && props.item.uuid) {
    loading.value = true
    const res = await cartStore.deleteCartItem(props.item.uuid)
    if (res.success) {
      emit('remove-cart-item', props.item.uuid)
    } else {
      // TODO handle error
      console.debug('error removing item', res.error)
    }
    loading.value = false
  }
}
function updateQuantity(num: number) {
  const quantity = new BigNumber(cartItem.value.quantity)
  cartItem.value.quantity = quantity.plus(num).toNumber()
  handleUpdateQuantity()
}
onMounted(() => {
  cartItem.value = cloneDeep(props.item)
  setItem(cartItem.value)
})
watch(() => props.item, (newValue, oldValue) => {
  if (newValue && !isEqual(newValue, oldValue)) {
    cartItem.value = cloneDeep(newValue)
    setItem(cartItem.value)
  }
})
</script>

<template>
  <li class="flex flex-row flex-wrap justify-between items-center px-4 py-6 sm:px-6 w-full gap-y-5">
    <div class="flex-shrink-0 text-tw-2 text-sm">
      <img
        :src="thumbnail"
        :alt="altText"
        class="w-20 rounded-md"
      >
    </div>
    <div class="ml-5 flex flex-1 flex-col align-center">
      <div class="flex">
        <div class="min-w-0 flex-1">
          <h4 class="text-sm font-medium text-tw-1">
            {{ item.name }}
          </h4>
        </div>
      </div>
      <div
        v-if="options.length || (extras && extras.length)"
        class="pt-1 text-sm text-tw-2"
      >
        <div v-if="options.length">
          <div
            v-for="option in options"
            :key="option.uuid"
          >
            {{ option.name }}
          </div>
        </div>
        <div v-if="extras && extras.length">
          <div
            v-for="extra in extras"
            :key="extra.uuid"
          >
            {{ extra.name }}
          </div>
        </div>
      </div>
      <div class="flex flex-1 items-end justify-between pt-2">
        <div class="mt-1">
          <p class="text-sm font-medium text-tw-2">
            {{ $filters.formatCurrency(subTotal, currencyCode) }}
          </p>
        </div>
      </div>
    </div>
    <div class="sm:ml-4 sm:w-auto w-full">
      <div
        v-if="loading"
        class="w-[135px] h-9 animate-pulse rounded-md border border-tw-1"
      >
        <div class="grid grid-cols-3 h-full">
          <!--          <div class="h-full border-r border-tw-1" /> -->
          <div class="h-full border-r border-tw-1">
            <div class="h-full w-full inline-flex items-center justify-center text-sm text-tw-2">
              {{ item.quantity }}
            </div>
          </div>
        </div>
      </div>
      <div
        v-else
        class="quantity-container"
      >
        <button
          v-if="cartItem.quantity <= 1"
          type="button"
          :disabled="disabled"
          :class="{ 'animate-pulse': loading }"
          class="rounded-l-md quantity-button"
          @click="removeItem"
        >
          <span class="sr-only">{{ t('REMOVE_X', { value: t('CART_ITEM') }) }}</span>
          <TrashIcon
            class="h-5 w-5"
            aria-hidden="true"
          />
        </button>
        <button
          v-if="cartItem.quantity > 1"
          type="button"
          :disabled="disabled"
          :class="{ 'animate-pulse': loading }"
          class="rounded-l-md quantity-button"
          @click="updateQuantity(-1)"
        >
          <span class="sr-only">{{ t('MINUS_ONE') }}</span>
          <ChevronLeftIcon
            class="h-5 w-5"
            aria-hidden="true"
          />
        </button>
        <button
          type="button"
          :disabled="true"
          class="-ml-px px-5"
        >
          {{ cartItem.quantity }}
        </button>
        <button
          :disabled="disabled || loading || cartItem.quantity >= 100"
          type="button"
          :class="{ 'animate-pulse': loading }"
          class="-ml-px rounded-r-md quantity-button"
          @click="updateQuantity(1)"
        >
          <span class="sr-only">{{ t('PLUS_ONE') }}</span>
          <ChevronRightIcon
            class="h-5 w-5"
            aria-hidden="true"
          />
        </button>
      </div>
    </div>
  </li>
</template>
